/* Express Storefront — interactive layer
 * Variant picker (pills + swatches), qty stepper, thumbnail gallery,
 * variant-aware main image, size-chart modal, async add-to-cart.
 * Vanilla JS, no framework, no theme dependencies.
 */
(function () {
  'use strict';

  const SELECTORS = {
    section: '.express',
    form: '[data-express-add-form]',
    optionValue: '[data-option-position]', // pills + swatches both have this
    optionGroup: '.express-product__option-values',
    qtyInput: '[data-qty-input]',
    qtyStep: '[data-qty-step]',
    variantInput: '[data-variant-input]',
    priceEl: '[data-product-price]',
    variantJson: '[data-variant-json]',
    sizeChartTrigger: '.express-size-chart-trigger',
    sizeChartModal: '#ExpressSizeChartModal',
    sizeChartImage: '[data-modal-image]',
    sizeChartTitle: '[data-modal-title]',
    sizeChartClose: '[data-close-modal]',
    addBtn: '.express-add-btn',
    toast: '#ExpressToast',
    mainImage: '[data-main-image]',
    thumb: '.express-thumb',
  };

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // HTML-escape an arbitrary string for safe interpolation into innerHTML.
  // Used for any cart-line field that originates from /cart.js (product_title,
  // variant_options, variant_title, etc.) — Shopify returns these verbatim
  // and a stray <, >, ", or & would otherwise break the markup or land as
  // live HTML. Escapes the five chars that matter inside element bodies and
  // double-quoted attribute values.
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function showToast(message, isError) {
    const el = document.querySelector(SELECTORS.toast);
    if (!el) return;
    el.textContent = message;
    el.classList.toggle('is-error', !!isError);
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(el._hideTimer);
    el._hideTimer = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => { el.hidden = true; }, 250);
    }, 2400);
  }

  function initForm(form) {
    const variantJsonScript = form.querySelector(SELECTORS.variantJson);
    if (!variantJsonScript) return;
    let variants = [];
    try { variants = JSON.parse(variantJsonScript.textContent); } catch (_) {}

    // AS Colour stock matrix — source of truth for real availability.
    // Shopify's variant.available is unreliable because products are set to
    // "continue selling when out of stock".
    let stockMatrix = null;
    const matrixEl = form.querySelector('[data-stock-matrix]');
    if (matrixEl) {
      try { stockMatrix = JSON.parse(matrixEl.textContent); } catch (_) {}
    }

    const variantInput = form.querySelector(SELECTORS.variantInput);
    const priceEl = form.querySelector(SELECTORS.priceEl);
    const addBtn = form.querySelector(SELECTORS.addBtn);
    const mainImg = form.querySelector(SELECTORS.mainImage);
    const defaultImgSrc = mainImg ? mainImg.dataset.defaultSrc : null;

    const selected = ['', '', ''];
    form.querySelectorAll(SELECTORS.optionValue + '.is-selected').forEach((el) => {
      const pos = parseInt(el.dataset.optionPosition, 10);
      if (pos >= 1 && pos <= 3) selected[pos - 1] = el.dataset.optionValue;
    });

    function findVariant() {
      return variants.find((v) => {
        return (
          (v.option1 || '') === (selected[0] || '') &&
          (v.option2 || '') === (selected[1] || '') &&
          (v.option3 || '') === (selected[2] || '')
        );
      });
    }

    /**
     * Real availability check — matrix wins, falls back to Shopify availability.
     * Assumes option1 = colour, option2 = size (AS Colour convention).
     */
    function isReallyAvailable(v) {
      if (!v) return false;
      if (stockMatrix && stockMatrix.colors) {
        const colorKey = String(v.option1 || '').toUpperCase().trim();
        const sizeKey = String(v.option2 || '').toUpperCase().trim();
        const colorEntry = stockMatrix.colors[colorKey];
        if (colorEntry && colorEntry[sizeKey]) {
          return colorEntry[sizeKey].inStock === true;
        }
      }
      return v.available === true;
    }

    function syncAvailability() {
      form.querySelectorAll(SELECTORS.optionValue).forEach((el) => {
        const pos = parseInt(el.dataset.optionPosition, 10);
        const value = el.dataset.optionValue;
        const trial = selected.slice();
        trial[pos - 1] = value;
        const v = variants.find((variant) => {
          return (
            (variant.option1 || '') === (trial[0] || '') &&
            (variant.option2 || '') === (trial[1] || '') &&
            (variant.option3 || '') === (trial[2] || '')
          );
        });
        const unavailable = !isReallyAvailable(v);
        el.classList.toggle('is-unavailable', unavailable);
      });
    }

    function syncAddBtn(v) {
      if (!addBtn) return;
      const label = addBtn.querySelector('.express-add-btn__label');
      if (!isReallyAvailable(v)) {
        addBtn.disabled = true;
        addBtn.classList.add('is-disabled');
        if (label) label.textContent = v ? 'Sold out' : 'Unavailable';
      } else {
        addBtn.disabled = false;
        addBtn.classList.remove('is-disabled');
        if (label) label.textContent = 'Add to cart';
      }
    }

    function syncMainImage(v) {
      if (!mainImg) return;
      if (v && v.featured_image && v.featured_image.src) {
        // Use Shopify CDN width=N query (works on every variant — webp, png,
        // pre-sized like ..._1024x.jpg?v=…). Preserve any existing v= cache
        // buster; strip a pre-existing width= so we don't compound.
        const raw = v.featured_image.src.replace(/[?&]width=\d+/g, '');
        const sep = raw.includes('?') ? '&' : '?';
        mainImg.src = raw + sep + 'width=800';
      } else if (defaultImgSrc) {
        mainImg.src = defaultImgSrc;
      }
    }

    function update() {
      const v = findVariant();
      if (v) {
        if (variantInput) variantInput.value = v.id;
        if (priceEl) priceEl.textContent = formatMoney(v.price);
      }
      syncAvailability();
      syncAddBtn(v);
      syncMainImage(v);
    }

    // Click handler: option pills/swatches AND thumbnails
    form.addEventListener('click', (e) => {
      // Option value (pill or swatch)
      const opt = e.target.closest(SELECTORS.optionValue);
      if (opt && form.contains(opt)) {
        const pos = parseInt(opt.dataset.optionPosition, 10);
        if (!pos) return;
        const group = opt.closest(SELECTORS.optionGroup);
        if (group) {
          group.querySelectorAll(SELECTORS.optionValue).forEach((sib) => {
            sib.classList.remove('is-selected');
            sib.setAttribute('aria-checked', 'false');
          });
        }
        opt.classList.add('is-selected');
        opt.setAttribute('aria-checked', 'true');
        selected[pos - 1] = opt.dataset.optionValue;
        update();
        return;
      }

      // Thumbnail
      const thumb = e.target.closest(SELECTORS.thumb);
      if (thumb && form.contains(thumb)) {
        e.preventDefault();
        const url = thumb.dataset.imageUrl;
        if (mainImg && url) mainImg.src = url;
        form.querySelectorAll(SELECTORS.thumb).forEach((t) => t.classList.remove('is-selected'));
        thumb.classList.add('is-selected');
        return;
      }

      // Qty stepper
      const qtyBtn = e.target.closest(SELECTORS.qtyStep);
      if (qtyBtn && form.contains(qtyBtn)) {
        const delta = parseInt(qtyBtn.dataset.qtyStep, 10) || 0;
        const input = form.querySelector(SELECTORS.qtyInput);
        if (!input) return;
        const next = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
        input.value = next;
        return;
      }
    });

    // Submit (async add-to-cart)
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const v = findVariant();
      if (!isReallyAvailable(v)) {
        showToast(v ? 'Sold out — choose another size' : 'That option is unavailable', true);
        return;
      }
      if (addBtn) addBtn.classList.add('is-loading');
      try {
        const formData = new FormData(form);
        const res = await fetch(form.action, {
          method: 'POST',
          headers: { 'Accept': 'application/javascript' },
          body: formData,
        });
        const data = await res.json();
        if (!res.ok) {
          showToast(data && data.description ? data.description : 'Could not add to cart', true);
          return;
        }
        // Auto-open the cart drawer to show what was added.
        document.dispatchEvent(new CustomEvent('cart:open'));
      } catch (err) {
        showToast('Network error — try again', true);
      } finally {
        if (addBtn) addBtn.classList.remove('is-loading');
      }
    });

    update();
  }

  function initSizeChart() {
    const modal = document.querySelector(SELECTORS.sizeChartModal);
    if (!modal) return;
    const imgEl = modal.querySelector(SELECTORS.sizeChartImage);
    const titleEl = modal.querySelector(SELECTORS.sizeChartTitle);

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest(SELECTORS.sizeChartTrigger);
      if (trigger) {
        const src = trigger.dataset.sizeChartImage;
        const title = trigger.dataset.sizeChartTitle || 'Size chart';
        if (imgEl && src) imgEl.src = src;
        if (titleEl) titleEl.textContent = title;
        if (typeof modal.showModal === 'function') {
          modal.showModal();
        } else {
          modal.setAttribute('open', '');
        }
        return;
      }
      const closer = e.target.closest(SELECTORS.sizeChartClose);
      if (closer && modal.contains(closer)) {
        if (typeof modal.close === 'function') modal.close(); else modal.removeAttribute('open');
      }
    });

    modal.addEventListener('click', (e) => {
      const inner = modal.querySelector('.express-size-chart-modal__inner');
      if (inner && !inner.contains(e.target)) {
        if (typeof modal.close === 'function') modal.close();
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // Cart drawer — fetch, render, qty +/-, remove, open/close
  // ─────────────────────────────────────────────────────────────────────
  function initCart() {
    const btn = document.getElementById('ExpressCartButton');
    const drawer = document.getElementById('ExpressCartDrawer');
    const backdrop = document.getElementById('ExpressCartBackdrop');
    if (!btn || !drawer || !backdrop) return;

    const countEl = btn.querySelector('[data-cart-count]');
    const itemsEl = drawer.querySelector('[data-cart-items]');
    const emptyEl = drawer.querySelector('[data-cart-empty]');
    const footerEl = drawer.querySelector('[data-cart-footer]');
    const subtotalEl = drawer.querySelector('[data-cart-subtotal]');

    function openDrawer() {
      drawer.hidden = false;
      backdrop.hidden = false;
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        backdrop.classList.add('is-visible');
        drawer.setAttribute('aria-hidden', 'false');
      });
      document.body.style.overflow = 'hidden';
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      backdrop.classList.remove('is-visible');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      setTimeout(() => {
        if (!drawer.classList.contains('is-open')) {
          drawer.hidden = true;
          backdrop.hidden = true;
        }
      }, 320);
    }

    function renderItems(cart) {
      // Clear existing items (keep the empty placeholder)
      itemsEl.querySelectorAll('.express-cart-item').forEach((el) => el.remove());

      if (!cart || !cart.items || cart.items.length === 0) {
        if (emptyEl) emptyEl.style.display = '';
        if (footerEl) footerEl.hidden = true;
        return;
      }
      if (emptyEl) emptyEl.style.display = 'none';
      if (footerEl) footerEl.hidden = false;

      cart.items.forEach((item) => {
        const article = document.createElement('article');
        article.className = 'express-cart-item';
        article.dataset.lineKey = item.key;

        const imgUrl = item.featured_image && item.featured_image.url
          ? item.featured_image.url
          : (item.image || '');
        const variantText = (item.variant_options && item.variant_options.length)
          ? item.variant_options.join(' · ')
          : '';
        const linePrice = formatMoney(item.final_line_price || item.line_price);

        // All free-text fields escaped before innerHTML — /cart.js returns
        // product_title / variant_options verbatim from Shopify, so a stray
        // quote or angle bracket would otherwise break the markup or land
        // as live HTML. linePrice is built locally from formatMoney() so
        // is already safe; quantity is coerced to a number string.
        article.innerHTML =
          '<div class="express-cart-item__media">' +
            (imgUrl ? '<img src="' + escapeHtml(imgUrl) + '" alt="" width="64" height="64">' : '') +
          '</div>' +
          '<div class="express-cart-item__body">' +
            '<h4 class="express-cart-item__title">' + escapeHtml(item.product_title) + '</h4>' +
            (variantText ? '<p class="express-cart-item__variant">' + escapeHtml(variantText) + '</p>' : '') +
            '<div class="express-cart-item__qty" role="group" aria-label="Quantity">' +
              '<button type="button" data-cart-qty-step="-1" aria-label="Decrease">&minus;</button>' +
              '<span>' + escapeHtml(item.quantity) + '</span>' +
              '<button type="button" data-cart-qty-step="1" aria-label="Increase">+</button>' +
            '</div>' +
          '</div>' +
          '<div class="express-cart-item__right">' +
            '<button type="button" class="express-cart-item__remove" data-cart-remove aria-label="Remove">&times;</button>' +
            '<span class="express-cart-item__price">' + linePrice + '</span>' +
          '</div>';

        itemsEl.appendChild(article);
      });

      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.items_subtotal_price || cart.total_price);
    }

    function updateCount(cart) {
      // Count visible items only — Express has no nested children, so all items count
      const n = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
      if (n > 0) {
        countEl.textContent = String(n);
        countEl.hidden = false;
      } else {
        countEl.hidden = true;
      }
    }

    async function fetchAndRender() {
      try {
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        const cart = await res.json();
        updateCount(cart);
        renderItems(cart);
        return cart;
      } catch (e) { /* swallow */ }
    }

    async function changeLine(key, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity }),
        });
        if (!res.ok) throw new Error('change failed');
      } catch (e) { /* swallow */ }
      await fetchAndRender();
    }

    // Open / close
    btn.addEventListener('click', () => { fetchAndRender(); openDrawer(); });
    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-close-cart]')) closeDrawer();
    });
    backdrop.addEventListener('click', closeDrawer);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });

    // Qty +/- and remove inside the drawer
    drawer.addEventListener('click', async (e) => {
      const stepBtn = e.target.closest('[data-cart-qty-step]');
      const removeBtn = e.target.closest('[data-cart-remove]');
      const item = e.target.closest('.express-cart-item');
      if (!item) return;
      const key = item.dataset.lineKey;
      if (!key) return;

      if (stepBtn) {
        const delta = parseInt(stepBtn.dataset.cartQtyStep, 10) || 0;
        const currentQty = parseInt(item.querySelector('.express-cart-item__qty span').textContent, 10) || 0;
        const next = Math.max(0, currentQty + delta);
        await changeLine(key, next);
      } else if (removeBtn) {
        await changeLine(key, 0);
      }
    });

    // External hooks — let other code refresh the cart
    document.addEventListener('cart:refresh', () => { fetchAndRender(); });
    document.addEventListener('cart:open', () => { openDrawer(); fetchAndRender(); });

    // Initial paint
    fetchAndRender();
  }

  // ─────────────────────────────────────────────────────────────────────
  // Entrance motion — fade-up products as they scroll into view.
  // Respects prefers-reduced-motion (CSS handles that branch by neutralising
  // the transition; we still add .is-revealed so opacity:1 sticks).
  // ─────────────────────────────────────────────────────────────────────
  function initMotion() {
    const targets = document.querySelectorAll('[data-fade-target]');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    targets.forEach((el) => io.observe(el));
  }

  function init() {
    document.querySelectorAll(SELECTORS.form).forEach(initForm);
    initSizeChart();
    initCart();
    initMotion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
